import { OpportunityLogEntryDTO } from '../../../api/models/Opportunities/OpportunityLogEntryDTO';
import { useTranslation } from 'react-i18next';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useApiClient } from '../../../hooks/useApiClient';
import useSalesforceStore from '../../../store/SalesforceStore';
import toaster from '../../../utils/toaster';
import OpportunityChat, { OpportunityChatHandle } from '../OpportunityChat/OpportunityChat';
import { useCallback, useRef } from 'react';


type OpportunityCreditMessageLogsProps = {
  isLoading?: boolean;
  logs: OpportunityLogEntryDTO[];
  className?: string;
}

const OpportunityCreditMessageLogs: React.FC<OpportunityCreditMessageLogsProps> = ({ logs, isLoading, className }) => {
  const { t } = useTranslation();
  const { apiClient } = useApiClient();
  const { opportunityData } = useSalesforceStore();
  const queryClient = useQueryClient();
  const chatRef = useRef<OpportunityChatHandle>(null);

  const { mutate: addMessage, isPending: isAdding } = useMutation({
    mutationFn: async (payload: any) => {
      await apiClient.salesforceOpportunityController.addLogMessage(opportunityData?.Id, payload.message, payload.area);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunityCreditData'] });
      chatRef.current?.scrollToBottom();
    },
    onError: (error) => {
      console.error('Error adding message:', error);
      toaster.error(t('failed_to_add_message'));
    },
  });

  const { mutate: updateMessage, isPending: isUpdating } = useMutation({
    mutationFn: async (payload: OpportunityLogEntryDTO) => {
      await apiClient.salesforceOpportunityController.updateLogMessage(opportunityData?.Id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunityCreditData'] });
    },
    onError: (error) => {
      console.error('Error adding message:', error);
      toaster.error(t('failed_to_pin_unpin_message'));
    },
  });

  const onUpdateMessage = useCallback(async (message: any) => {
    try {
      await updateMessage(message)
      if (chatRef.current && message?.time) {
        chatRef.current?.scrollToMessage(message.time);
      }
    } catch (error) {
      console.warn(error)
    }

  }, [updateMessage])

  return (
    <OpportunityChat
      ref={chatRef}
      dealer={opportunityData?.Nome_partner__c}
      logs={logs}
      className={className}
      addMessage={addMessage}
      updateMessage={onUpdateMessage}
      isLoading={isLoading}
      isAdding={isAdding}
      isUpdating={isUpdating}
      showLastMessage={false}
    />
  );
};

export default OpportunityCreditMessageLogs;
